// Global Zones Page - Revamped to match Customers table design

import React, { useMemo, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useGetGlobalZones } from 'services/queries/use-get-global-zones'
import { Zone } from 'types'
import GlobalZonesTable from './global-zones-table'
import CreateOrEditZone from './create-or-edit-zone'

const GlobalZones: React.FC = () => {
  const { data: globalZones, isLoading: isGlobalZonesLoading } =
    useGetGlobalZones()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [zoneToEdit, setZoneToEdit] = useState<Zone | undefined>(undefined)

  // Transform data: de-duplicate countries per zone and sort zones by name so
  // they always render A, B, C, … (and custom zones alphabetically after).
  const zonesData: Zone[] = useMemo(() => {
    if (!globalZones?.data) return []
    return [...globalZones.data]
      .map((zone) => ({
        ...zone,
        countries: Array.from(new Set(zone.countries)),
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [globalZones])

  const handleCreate = () => {
    setZoneToEdit(undefined)
    setDialogOpen(true)
  }

  const handleEdit = (zone: Zone) => {
    setZoneToEdit(zone)
    setDialogOpen(true)
  }

  // Handle loading state
  if (isGlobalZonesLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '300px',
          gap: 2,
        }}
      >
        <CircularProgress size={30} />
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          Loading zones...
        </Typography>
      </Box>
    )
  }

  // Handle case where globalZones data is not available
  if (!globalZones?.data) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '300px',
        }}
      >
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
          No zones data available
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <GlobalZonesTable
        data={zonesData}
        onCreateZone={handleCreate}
        onEditZone={handleEdit}
      />
      <CreateOrEditZone
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        existingZones={zonesData}
        zoneToEdit={zoneToEdit}
      />
    </>
  )
}

export default GlobalZones
